import { useEffect, useRef, useState } from "react"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { BRIEF_COPY } from "./copy"

export function Eyebrow({ className, children }) {
  return (
    <span className={cn("block text-[10.5px] font-bold tracking-[0.1em] text-ink-3 uppercase", className)}>
      {children}
    </span>
  )
}

// Text the customer wrote (design LongText): shown as written, line breaks
// kept, clamped to `lines` with a fade and a Show more only when it runs past
// them. The requirements are the `main` text: larger, and nine lines deep.
// `label` is optional; `textClassName` replaces the default type styling.
export default function LongText({ label, text, lines = 4, main = false, textClassName }) {
  const bodyRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [clamped, setClamped] = useState(false)

  useEffect(() => {
    const body = bodyRef.current
    if (!body || open) return undefined

    const measure = () => setClamped(body.scrollHeight > body.clientHeight + 2)
    measure()
    if (typeof ResizeObserver === "undefined") return undefined

    const observer = new ResizeObserver(measure)
    observer.observe(body)
    return () => observer.disconnect()
  }, [open, text, lines])

  return (
    <div>
      {label && <Eyebrow className="mb-[7px]">{label}</Eyebrow>}
      <div
        ref={bodyRef}
        style={open ? undefined : { maxHeight: `calc(${lines} * 1.65em)` }}
        className={cn(
          "overflow-hidden leading-[1.65] text-pretty whitespace-pre-line [overflow-wrap:anywhere]",
          textClassName ??
            (main ? "text-[14.5px] text-foreground max-[560px]:text-[14px]" : "text-[13.5px] text-muted-foreground"),
          clamped && !open && "[mask-image:linear-gradient(black_55%,transparent)]"
        )}
      >
        {text}
      </div>
      {clamped && (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
          className="mt-2 inline-flex items-center gap-[5px] py-1 text-[12.5px] font-semibold text-foreground hover:text-primary"
        >
          {open ? BRIEF_COPY.showLess : BRIEF_COPY.showMore}
          <ChevronDown className={cn("size-[15px] transition-transform", open && "rotate-180")} strokeWidth={2} />
        </button>
      )}
    </div>
  )
}

// A run of long texts, each split from the one above by a hairline.
export function LongTexts({ items = [], className }) {
  if (!items.length) return null

  return (
    <div className={cn("flex flex-col gap-[18px] [&>*+*]:border-t [&>*+*]:border-border [&>*+*]:pt-[18px]", className)}>
      {items.map((item) => (
        <LongText key={item.label} label={item.label} text={item.text} />
      ))}
    </div>
  )
}
